import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Checkbox, FormControlLabel, TextField, IconButton,
  Tabs, Tab, Chip, Alert, CircularProgress, Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import EventBusyIcon from '@mui/icons-material/EventBusy';

const API = process.env.REACT_APP_API_URL || '';
const DIAS = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];

const fieldSx = {
  '& .MuiOutlinedInput-root fieldset': { borderColor: 'var(--c-primario)' },
  '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'var(--c-acento)' },
};
const labelStyle = { style: { color: 'var(--c-acento)' }, shrink: true };
const inputStyle = { style: { color: '#e0e0e0' } };

const AUSENCIA_VACIA = { fecha_inicio: '', fecha_fin: '', motivo: '' };

export default function Disponibilidad({ medicoId, medicoNombre, onClose }) {
  const [tab, setTab] = useState(0);

  // ── Horarios — array de {dia, desde, hasta, duracion}; puede haber 2 por día ──
  const [horarios, setHorarios] = useState([]);

  // ── Ausencias ──
  const [ausencias, setAusencias] = useState([]);
  const [formAusencia, setFormAusencia] = useState(AUSENCIA_VACIA);
  const [turnosAfectados, setTurnosAfectados] = useState(null);
  const [verificando, setVerificando] = useState(false);
  const [guardandoAusencia, setGuardandoAusencia] = useState(false);
  const [errorAusencia, setErrorAusencia] = useState('');

  useEffect(() => {
    cargarHorarios();
    cargarAusencias();
  }, [medicoId]);

  async function cargarHorarios() {
    try {
      const r = await axios.get(`${API}/api/medicos/${medicoId}/disponibilidad`);
      setHorarios(r.data || []);
    } catch { setHorarios([]); }
  }

  async function cargarAusencias() {
    try {
      const r = await axios.get(`${API}/api/medicos/${medicoId}/ausencias`);
      setAusencias(r.data || []);
    } catch { setAusencias([]); }
  }

  async function guardarHorarios() {
    try {
      await axios.post(`${API}/api/medicos/${medicoId}/disponibilidad`, { horarios });
      onClose();
    } catch (err) { console.error(err); }
  }

  // ── Lógica de horarios ────────────────────────────────────────────────────

  // Bloques del día = todas las entradas del array cuyo campo dia === dia
  function bloquesDia(dia) {
    return horarios
      .map((h, i) => ({ ...h, _idx: i }))
      .filter(h => h.dia === dia);
  }

  function toggleDia(dia) {
    const activo = horarios.some(h => h.dia === dia);
    if (activo) {
      // Desactivar: quitar todos los bloques de ese día
      setHorarios(horarios.filter(h => h.dia !== dia));
    } else {
      // Activar: agregar un bloque por defecto
      setHorarios([...horarios, { dia, desde: '08:00', hasta: '12:00', duracion: 30 }]);
    }
  }

  function actualizarBloque(globalIdx, campo, valor) {
    setHorarios(horarios.map((h, i) => i === globalIdx ? { ...h, [campo]: valor } : h));
  }

  function dividirEnDos(dia) {
    // Agrega un segundo bloque para ese día con horario de tarde por defecto
    setHorarios([...horarios, { dia, desde: '14:00', hasta: '18:00', duracion: 30 }]);
  }

  function eliminarBloque(globalIdx) {
    setHorarios(horarios.filter((_, i) => i !== globalIdx));
  }

  // ── Ausencias ─────────────────────────────────────────────────────────────

  async function verificarYMostrar() {
    if (!formAusencia.fecha_inicio || !formAusencia.fecha_fin) {
      setErrorAusencia('Completá las dos fechas');
      return;
    }
    if (formAusencia.fecha_fin < formAusencia.fecha_inicio) {
      setErrorAusencia('La fecha de fin debe ser posterior al inicio');
      return;
    }
    setVerificando(true);
    setTurnosAfectados(null);
    setErrorAusencia('');
    try {
      const r = await axios.get(
        `${API}/api/medicos/${medicoId}/ausencias/conflictos?fecha_inicio=${formAusencia.fecha_inicio}&fecha_fin=${formAusencia.fecha_fin}`
      );
      const lista = r.data.turnos || [];
      if (lista.length === 0) {
        await guardarAusencia(false);
      } else {
        setTurnosAfectados(lista);
      }
    } catch (err) {
      setErrorAusencia(err.response?.data?.error || 'Error al verificar');
    } finally {
      setVerificando(false);
    }
  }

  async function guardarAusencia(cancelarTurnos) {
    if (!formAusencia.fecha_inicio || !formAusencia.fecha_fin) {
      setErrorAusencia('Las fechas son obligatorias');
      return;
    }
    setGuardandoAusencia(true);
    setErrorAusencia('');
    try {
      await axios.post(`${API}/api/medicos/${medicoId}/ausencias`, {
        ...formAusencia,
        cancelar_turnos: cancelarTurnos,
      });
      setFormAusencia(AUSENCIA_VACIA);
      setTurnosAfectados(null);
      cargarAusencias();
    } catch (err) {
      setErrorAusencia(err.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardandoAusencia(false);
    }
  }

  async function eliminarAusencia(id) {
    if (!window.confirm('¿Eliminar esta ausencia? El médico vuelve a estar disponible esos días.')) return;
    try {
      await axios.delete(`${API}/api/medicos/${medicoId}/ausencias/${id}`);
      cargarAusencias();
    } catch (err) { console.error(err); }
  }

  function formatFecha(f) {
    if (!f) return '—';
    return String(f).split('T')[0].split('-').reverse().join('/');
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: '#16213e', color: '#e0e0e0' } }}>
      <DialogTitle sx={{ color: '#90caf9', pb: 0 }}>
        {medicoNombre}
      </DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
        px: 3, borderBottom: '1px solid #0f3460',
        '& .MuiTab-root': { color: '#b0b0b0' },
        '& .Mui-selected': { color: '#90caf9' },
        '& .MuiTabs-indicator': { bgcolor: '#90caf9' },
      }}>
        <Tab label="Horarios habituales" />
        <Tab label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EventBusyIcon sx={{ fontSize: 16 }} />
            Ausencias
            {ausencias.length > 0 && (
              <Chip label={ausencias.length} size="small"
                sx={{ bgcolor: '#ff1744', color: '#fff', fontSize: 10, height: 18, ml: 0.5 }} />
            )}
          </Box>
        } />
      </Tabs>

      <DialogContent sx={{ pt: 2 }}>

        {/* ── TAB HORARIOS ── */}
        {tab === 0 && (
          <Box>
            <Typography variant="body2" sx={{ color: '#b0b0b0', mb: 2 }}>
              Seleccioná los días que trabaja. Podés dividir un día en dos turnos (mañana y tarde).
            </Typography>
            {DIAS.map(dia => {
              const bloques = bloquesDia(dia);
              const activo = bloques.length > 0;
              return (
                <Box key={dia} sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: activo ? '#0f3460' : '#1a2a4a', border: '1px solid #0f3460' }}>
                  {/* Checkbox del día */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <FormControlLabel
                      control={
                        <Checkbox checked={activo} onChange={() => toggleDia(dia)}
                          sx={{ color: '#90caf9', '&.Mui-checked': { color: '#90caf9' } }} />
                      }
                      label={<Typography sx={{ color: '#e0e0e0', fontWeight: activo ? 600 : 400 }}>{dia}</Typography>}
                    />
                    {/* Botón "Dividir en dos" — solo si hay 1 bloque */}
                    {activo && bloques.length === 1 && (
                      <Tooltip title="Agregar turno de tarde (dividir en dos bloques horarios)">
                        <Button
                          size="small"
                          startIcon={<CallSplitIcon sx={{ fontSize: 16 }} />}
                          onClick={() => dividirEnDos(dia)}
                          sx={{
                            color: '#90caf9', fontSize: 11, textTransform: 'none',
                            border: '1px dashed #0f3460',
                            '&:hover': { bgcolor: 'rgba(144,202,249,0.08)', borderColor: '#90caf9' },
                          }}
                        >
                          Dividir en dos
                        </Button>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Bloques del día */}
                  {bloques.map((bloque, bloqueNum) => (
                    <Box key={bloque._idx} sx={{ mt: 1.5 }}>
                      {bloques.length > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography sx={{ fontSize: 11, color: '#90caf9', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {bloqueNum === 0 ? 'Turno mañana' : 'Turno tarde'}
                          </Typography>
                          {/* Botón eliminar — solo si hay más de 1 bloque */}
                          <Tooltip title="Eliminar este bloque">
                            <IconButton size="small" onClick={() => eliminarBloque(bloque._idx)}
                              sx={{ color: '#f48fb1', p: 0.5 }}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField label="Desde" type="time" value={bloque.desde}
                          onChange={e => actualizarBloque(bloque._idx, 'desde', e.target.value)}
                          InputLabelProps={labelStyle} inputProps={inputStyle}
                          sx={{ width: 130, ...fieldSx }} />
                        <TextField label="Hasta" type="time" value={bloque.hasta}
                          onChange={e => actualizarBloque(bloque._idx, 'hasta', e.target.value)}
                          InputLabelProps={labelStyle} inputProps={inputStyle}
                          sx={{ width: 130, ...fieldSx }} />
                        <TextField label="Duración (min)" type="number" value={bloque.duracion}
                          onChange={e => actualizarBloque(bloque._idx, 'duracion', parseInt(e.target.value) || 30)}
                          InputLabelProps={labelStyle} inputProps={{ ...inputStyle, min: 10, max: 120 }}
                          sx={{ width: 150, ...fieldSx }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        )}

        {/* ── TAB AUSENCIAS ── */}
        {tab === 1 && (
          <Box>
            {ausencias.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: '#90caf9', fontWeight: 600, mb: 1, fontSize: 14 }}>
                  Ausencias programadas
                </Typography>
                {ausencias.map(a => (
                  <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    p: 1.5, mb: 1, bgcolor: '#1a2a4a', borderRadius: 2, border: '1px solid #ff1744' }}>
                    <Box>
                      <Typography sx={{ fontSize: 14, color: '#e0e0e0' }}>
                        {formatFecha(a.fecha_inicio)} → {formatFecha(a.fecha_fin)}
                      </Typography>
                      {a.motivo && (
                        <Typography sx={{ fontSize: 12, color: '#b0b0b0' }}>{a.motivo}</Typography>
                      )}
                    </Box>
                    <IconButton onClick={() => eliminarAusencia(a.id)} sx={{ color: '#f48fb1' }} size="small">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            <Typography sx={{ color: '#90caf9', fontWeight: 600, mb: 1.5, fontSize: 14 }}>
              Agregar ausencia
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Desde *" type="date" value={formAusencia.fecha_inicio} fullWidth
                  onChange={e => { setFormAusencia({ ...formAusencia, fecha_inicio: e.target.value }); setTurnosAfectados(null); setErrorAusencia(''); }}
                  InputLabelProps={{ shrink: true, style: { color: '#90caf9', backgroundColor: 'var(--c-superficie)', paddingLeft: 4, paddingRight: 4 } }}
                  inputProps={{ style: { color: '#e0e0e0', colorScheme: 'dark' } }} sx={fieldSx} />
                <TextField label="Hasta *" type="date" value={formAusencia.fecha_fin} fullWidth
                  onChange={e => { setFormAusencia({ ...formAusencia, fecha_fin: e.target.value }); setTurnosAfectados(null); setErrorAusencia(''); }}
                  InputLabelProps={{ shrink: true, style: { color: '#90caf9', backgroundColor: 'var(--c-superficie)', paddingLeft: 4, paddingRight: 4 } }}
                  inputProps={{ style: { color: '#e0e0e0', colorScheme: 'dark' } }} sx={fieldSx} />
              </Box>
              <TextField label="Motivo (vacaciones, licencia, congreso...)" value={formAusencia.motivo}
                onChange={e => setFormAusencia({ ...formAusencia, motivo: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />

              {errorAusencia && (
                <Typography sx={{ color: '#f44336', fontSize: 13 }}>{errorAusencia}</Typography>
              )}

              {turnosAfectados !== null && turnosAfectados.length > 0 && (
                <Alert severity="warning" sx={{ fontSize: 13 }}>
                  <strong>{turnosAfectados.length} turno{turnosAfectados.length > 1 ? 's' : ''} en ese período:</strong>
                  <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                    {turnosAfectados.slice(0, 5).map(t => (
                      <li key={t.id} style={{ fontSize: 12 }}>
                        {formatFecha(t.fecha)} {String(t.hora_inicio).substring(0, 5)} — {t.paciente_nombre}
                      </li>
                    ))}
                    {turnosAfectados.length > 5 && (
                      <li style={{ fontSize: 12 }}>...y {turnosAfectados.length - 5} más</li>
                    )}
                  </Box>
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {turnosAfectados && turnosAfectados.length > 0 ? (
                  <>
                    <Button variant="outlined" disabled={guardandoAusencia} onClick={() => guardarAusencia(false)}
                      sx={{ color: '#90caf9', borderColor: '#0f3460', fontSize: 12 }}>
                      Guardar sin cancelar turnos
                    </Button>
                    <Button variant="contained" disabled={guardandoAusencia} onClick={() => guardarAusencia(true)}
                      sx={{ bgcolor: '#ff1744', '&:hover': { bgcolor: '#d50000' }, fontSize: 12 }}>
                      {guardandoAusencia ? 'Guardando...' : `Guardar y cancelar ${turnosAfectados.length} turno${turnosAfectados.length > 1 ? 's' : ''}`}
                    </Button>
                  </>
                ) : (
                  <Button variant="contained" startIcon={verificando ? null : <AddIcon />}
                    disabled={guardandoAusencia || verificando}
                    onClick={verificarYMostrar}
                    sx={{ bgcolor: '#0f3460', '&:hover': { bgcolor: '#1a4a8a' } }}>
                    {verificando
                      ? <><CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />Verificando...</>
                      : guardandoAusencia ? 'Guardando...' : 'Agregar ausencia'}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#90caf9' }}>Cerrar</Button>
        {tab === 0 && (
          <Button onClick={guardarHorarios} variant="contained"
            sx={{ bgcolor: '#0f3460', '&:hover': { bgcolor: '#1a4a8a' } }}>
            Guardar horarios
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
